import React from 'react'
import { Box, Typography } from '@mui/material'
import MusicNoteIcon from '@mui/icons-material/MusicNote'
import SelfImprovementIcon from '@mui/icons-material/SelfImprovement'
import ExtensionIcon from '@mui/icons-material/Extension'
import BubbleChartIcon from '@mui/icons-material/BubbleChart'

const ACTIVITIES = [
  {
    id: 'music',
    name: 'Music',
    icon: MusicNoteIcon,
    color: '#8b5cf6',
    description: 'Relaxing sounds',
  },
  {
    id: 'breathing',
    name: 'Breathing',
    icon: SelfImprovementIcon,
    color: '#06b6d4',
    description: 'Guided exercise',
  },
  {
    id: 'memory',
    name: 'Memory',
    icon: ExtensionIcon,
    color: '#10b981',
    description: 'Match pairs',
  },
  {
    id: 'bubbles',
    name: 'Bubbles',
    icon: BubbleChartIcon,
    color: '#f59e0b',
    description: 'Pop bubbles',
  },
]

export default function EngagementSelector({ 
  selectedActivity, 
  onSelect,
  enabledActivities = ['music', 'breathing', 'memory', 'bubbles'],
}) {
  const availableActivities = ACTIVITIES.filter(a => enabledActivities.includes(a.id))

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: { xs: 1.5, sm: 2 },
        width: '100%',
      }}
    >
      <Typography
        sx={{
          fontSize: { xs: '11px', sm: '12px', lg: '13px' },
          fontWeight: 600,
          color: '#6b7280',
          textAlign: 'center',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
        }}
      >
        While you wait...
      </Typography>

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: { xs: 1, sm: 1.5 },
          justifyContent: 'center',
        }}
      >
        {availableActivities.map((activity) => {
          const Icon = activity.icon
          const isSelected = selectedActivity === activity.id

          return (
            <Box
              key={activity.id}
              onClick={() => onSelect(isSelected ? null : activity.id)}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: { xs: 0.25, sm: 0.5 },
                padding: { xs: '8px 10px', sm: '10px 14px', lg: '12px 16px' },
                borderRadius: { xs: '12px', sm: '14px', lg: '16px' },
                cursor: 'pointer',
                minWidth: { xs: '60px', sm: '65px', lg: '70px' },
                background: isSelected 
                  ? `linear-gradient(135deg, ${activity.color}15 0%, ${activity.color}10 100%)`
                  : '#ffffff',
                border: isSelected 
                  ? `2px solid ${activity.color}`
                  : '2px solid #e5e7eb',
                boxShadow: isSelected
                  ? `0 4px 12px ${activity.color}20`
                  : '0 1px 3px rgba(0, 0, 0, 0.05)',
                transition: 'all 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: isSelected
                    ? `0 6px 16px ${activity.color}30`
                    : '0 4px 12px rgba(0, 0, 0, 0.1)',
                  borderColor: activity.color,
                },
                '&:active': {
                  transform: 'scale(0.98)',
                },
              }}
            >
              <Box
                sx={{
                  width: { xs: '32px', sm: '36px', lg: '40px' },
                  height: { xs: '32px', sm: '36px', lg: '40px' },
                  borderRadius: { xs: '10px', sm: '11px', lg: '12px' },
                  background: isSelected
                    ? `linear-gradient(135deg, ${activity.color} 0%, ${activity.color}cc 100%)`
                    : `${activity.color}15`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease',
                }}
              >
                <Icon 
                  sx={{ 
                    fontSize: { xs: '18px', sm: '20px', lg: '22px' }, 
                    color: isSelected ? '#ffffff' : activity.color,
                    transition: 'color 0.2s ease',
                  }} 
                />
              </Box>
              <Typography
                sx={{
                  fontSize: { xs: '10px', sm: '11px', lg: '12px' },
                  fontWeight: 600,
                  color: isSelected ? activity.color : '#374151',
                  transition: 'color 0.2s ease',
                }}
              >
                {activity.name}
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: '8px', sm: '9px', lg: '10px' },
                  color: '#9ca3af',
                  textAlign: 'center',
                  display: { xs: 'none', sm: 'block' },
                }}
              >
                {activity.description}
              </Typography>
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}
